import PureCampaignBuilder from "@/Apps/Funnels/CampaignBuilder/PureCampaignBuilder";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { CaretRight, PaintBrush, TreeStructure, Wrench } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import Calout from "@/ui/Calout";
import { MobileState } from "@/Apps/Pages/MobileResponsiveToggle/types";
import { EditorMode } from "@/Apps/Pages/V2Editor/types";
import { addBodyIfNone } from "../../PageBuilderArtifact";
import { cleanThisContent } from "@/Apps/Pages/V2Editor/actions";
import { cleanFromLangGraph } from "@/Apps/AITools/LangGraphBot/LangGraphBot.stories";
import PureCanvas from "@/Apps/Pages/V1Editor/PureCanvas";
import { PageContent } from "@/Apps/Pages/Page/Page";
import { NiceBadge } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/PromptMessageContainer";

export const CampaignBuilderFromStructuredMessage = (props: any) => {
  const { payload, currentThread, setArtifactOptions, artifactOptions } = props;
  const campaignBuilderRef = useRef<any>(null);
  const { campaignContent = [] } = payload;

  // Throttle fitView to only trigger once per half second
  useEffect(() => {
    let lastCalled = (window as any).__campaignBuilderFitViewLastCalled || 0;
    const now = Date.now();
    if (now - lastCalled > 500) {
      if (
        campaignBuilderRef.current &&
        typeof campaignBuilderRef.current.fitView === "function"
      ) {
        campaignBuilderRef.current.fitView();
        (window as any).__campaignBuilderFitViewLastCalled = now;
      }
    }
  }, []);

  return (
    <div className="h-96 w-full border border-black/20 dark:border-white/20 rounded-md px-4 py-2 flex flex-col gap-4">
      <div className="flex flex-row gap-2 items-center justify-between">
        <div className="flex flex-row gap-2 items-center">
          <TreeStructure size={24} />
          <p className="text-sm font-medium">Campaign Builder</p>
          <p className="text-sm text-gray-500">
            {campaignContent.length} campaign items
          </p>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <Button
            label={
              <div className="flex gap-1 items-center">
                <CaretRight size={14} weight="fill" color="#AAA" />{" "}
                {/* Tailwind gray-300 */}
                See in Campaign Builder
              </div>
            }
            type={ButtonTypes.SOFT}
            onClick={() => {
              console.log("apply changes");
              setArtifactOptions({
                ...artifactOptions,
                isShowing: true,
                container: {
                  ...artifactOptions.container,
                  type: "campaign_builder",
                  title: "Campaign Builder",
                  // pageContent: [],
                  campaignContent,
                },
              });
              // artifactOptions?.ref?.current?.fitView();
            }}
          />
        </div>
      </div>

      <PureCampaignBuilder
        ref={campaignBuilderRef}
        objects={campaignContent}
        id="campaign-builder"
        readOnly
        showControls={false}
        showMiniMap={false}
      />
    </div>
  );
};

export const CampaignBuilderArtboardFromStructuredMessage = (props: any) => {
  const { payload, currentThread, setArtifactOptions, artifactOptions } = props;
  // const { campaignContent = [] } = payload;
  const campaignContent = payload?.campaignContent || [];
  
  // Add debugging to understand when the component renders
  // console.log("CampaignBuilderArtboardFromStructuredMessage render:", {
  //   campaignContentLength: campaignContent.length,
  //   payloadKeys: Object.keys(payload || {}),
  //   hasPageComponents: campaignContent.filter((itm: any) => itm.type === "PageComponent").length
  // });

  // Use state to ensure stable rendering during streaming
  const [stableCampaignContent, setStableCampaignContent] = useState(campaignContent);
  
  useEffect(() => {
    if (campaignContent && campaignContent.length > 0) {
      setStableCampaignContent(campaignContent);
    }
  }, [campaignContent]);

  // On first mount, clear pageContent in artifactOptions.container
  useEffect(() => {
    // Only run on first mount
    if (artifactOptions && setArtifactOptions) {
      setArtifactOptions({
        ...artifactOptions,
        nodeId: payload?.nodeId,
        // isShowing: true,
        container: {
          ...artifactOptions.container,
          // If the existing nodeId in artifactOptions is different from the payload's nodeId, clear pageContent
          pageContent:
            artifactOptions?.nodeId && artifactOptions.nodeId !== payload?.nodeId
              ? []
              : artifactOptions?.container?.pageContent,
          // nodeId: payload?.nodeId,
          // pageContent: [],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageClick =
    (id: string) => (e: React.MouseEvent<HTMLDivElement>) => {
      console.log("page clicked", id, stableCampaignContent.find((itm: any) => itm.id === id));
      const content = stableCampaignContent.find((itm: any) => itm.id === id)?.content;
      const sanitizedContent = addBodyIfNone(cleanThisContent(cleanFromLangGraph(content)));
      const options = {
        ...artifactOptions,
        isShowing: true,
        container: {
          ...artifactOptions.container,
          type: "page_builder",
          // title: "Page Builder",
          title:
            stableCampaignContent.find((itm: any) => itm.id === id)?.name ||
            "Page Builder",
          pageContent: sanitizedContent,
        },
      };
      console.log("handlePageClick - options", options);
      setArtifactOptions(options);
    };

  return (
    <div
      key={`campaign-artboard-${stableCampaignContent.length}-${stableCampaignContent.map((item: any) => item.id).join("-")}`}
      className="w-full border border-black/20 dark:border-white/20  rounded-md px-4 py-2 flex flex-col gap-4"
    >
      <div className="flex flex-row gap-2 items-center justify-between">
        <div className="flex flex-row gap-2 items-center">
          <TreeStructure size={24} />
          <p className="text-sm font-medium">Campaign Builder</p>
          <p className="text-sm text-gray-500">
            {
              stableCampaignContent.filter(
                (itm: any) => itm.type === "PageComponent"
              ).length
            }{" "}
            pages
          </p>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <Button
            label={
              <div className="flex gap-1 items-center">
                <CaretRight size={14} weight="fill" color="#AAA" />{" "}
                {/* Tailwind gray-300 */}
                See in Campaign Builder
              </div>
            }
            type={ButtonTypes.SOFT}
            onClick={() => {
              console.log("apply changes", stableCampaignContent);
              setArtifactOptions({
                ...artifactOptions,
                isShowing: true,
                container: {
                  ...artifactOptions.container,
                  type: "campaign_builder",
                  title: "Campaign Builder",
                  // pageContent: [],
                  campaignContent: stableCampaignContent,
                },
              });
            }}
          />
        </div>
      </div>
      <Calout title="Here are the pages in your campaign.  Click one to open it in the editor." />
      <div className="w-full flex justify-center">
        <div className="w-full overflow-y-auto h-48 mb-2">
          {/* <div className="flex gap-4 min-w-[800px] mb-2"> */}
          <div className="flex gap-4 grid grid-cols-3 mb-2">
            {stableCampaignContent &&
              stableCampaignContent.length > 0 &&
              stableCampaignContent
                .filter((itm: any) => itm.type === "PageComponent")
                .map((itm: any) => (
                  <div
                    key={itm.id}
                    className="border border-black/20 dark:border-white/20 rounded-md bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20 transition-colors duration-200 cursor-pointer overflow-hidden cursor-pointer"
                  >
                    <PageThumbnail {...itm} onClick={handlePageClick(itm.id)} />
                  </div>
                ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BuildingNodeContent = (props: any) => {
  const { payload, currentThread, setArtifactOptions, artifactOptions } = props;
  const { nodeId } = payload;

  useEffect(() => {
    console.log("BuildingNodeContent", payload);
    setArtifactOptions({
      ...artifactOptions,
      isShowing: true,
      container: {
        ...artifactOptions.container,
        nodeId,
        pageContent: [],
      },
    });
  }, [nodeId]);

  return <NiceBadge caption={`Building: Funnel Content`} icon={<Wrench className="w-4 h-4" />} />;
};

export const PageThumbnail = (props: any) => {
  const { id, width = 275, height = 175, content, onClick = () => {} } = props;
  return (
    <div
      className="text-sm overflow-hidden rounded-md flex items-center justify-center"
      onClick={onClick}
      style={{ width, height }}
    >
      {content && (
        <div style={{ width, height }} className="overflow-hidden relative">
          <div
            style={{
              transform: `scale(${width / 1200})`,
              transformOrigin: "top left",
              width: 1200,
              height: 652, // typical aspect ratio
            }}
            className="bg-white"
          >
            <PureCanvas
              content={addBodyIfNone(
                cleanThisContent(cleanFromLangGraph(content))
              )}
              mobileState={{ type: MobileState.FULLSCREEN }}
              mode={EditorMode.EDITOR}
              onChange={() => null}
              onSave={() => null}
              onLoad={() => null}
              onItemSelect={() => null}
              disableDefaultProperties={true}
              onShowProperties={() => null}
              onHideProperties={() => null}
              setActiveAddNewTab={() => null}
              render={() => null}
            >
              {() => <div></div>}
            </PureCanvas>
            {/* <p className="text-sm text-gray-500">{label}</p> */}
          </div>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              pointerEvents: "all",
              userSelect: "none",
            }}
            className="hover:bg-black/20 dark:hover:bg-white/20 transition-colors duration-200"
            onClick={() => onClick(id)}
          />
        </div>
      )}
    </div>
  );
};
